import { Box, Grid, Typography } from '@mui/material';
import { ResponsiveBar } from '@nivo/bar';
import { Surface } from '../../../components/primitives';
import ChartSection from '../shared/ChartSection';
import ChartTooltip from '../shared/ChartTooltip';
import useNivoTheme from '../shared/useNivoTheme';

export default function DisclosureDelay({ data }) {
    const { theme, colors } = useNivoTheme();
    if (!data) return null;

    return (
        <ChartSection id="disclosure-delay" title="How long do they wait to disclose their trades?" subtitle={data.violation_pct + '% of trades are disclosed after the 45-day STOCK Act deadline'} takeaway={`The average Congress member waits ${data.avg_delay_days} days to disclose a trade, and ${data.violation_pct}% of all trades violate the 45-day STOCK Act deadline — with virtually no enforcement consequences.`}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
                {[
                    { label: 'Avg Delay', value: data.avg_delay_days + ' days' },
                    { label: 'STOCK Act Violations', value: data.violations_45_day.toLocaleString(), color: 'error.main' },
                    { label: 'Median Delay', value: data.median_delay_days + ' days' },
                ].map((s, i) => (
                    <Grid item xs={4} key={i}>
                        <Surface variant="subtle" padding={2}>
                            <Typography variant="overline" sx={{ color: 'text.muted' }}>{s.label}</Typography>
                            <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, color: s.color || 'text.primary', fontFamily: t => t.typography.fontFamilyMono }}>{s.value}</Typography>
                        </Surface>
                    </Grid>
                ))}
            </Grid>
            <Box sx={{ height: 280 }}>
                <ResponsiveBar
                    data={data.histogram} keys={['count']} indexBy="days"
                    margin={{ top: 10, right: 20, bottom: 40, left: 45 }}
                    padding={0.15} borderRadius={2} enableLabel={false}
                    colors={({ data: d }) => d.days > 45 ? colors.error : colors.primary}
                    axisBottom={{ format: v => v + 'd', legend: 'Days to disclose (red = STOCK Act violation)', legendPosition: 'middle', legendOffset: 32 }}
                    axisLeft={{ legend: 'Trades', legendPosition: 'middle', legendOffset: -35 }}
                    tooltip={({ data: d, color }) => (
                        <ChartTooltip
                            title={`${d.days} days`}
                            rows={[
                                { label: 'Trades', value: d.count.toLocaleString(), color },
                                ...(d.days > 45
                                    ? [{ label: 'Status', value: 'STOCK Act violation', valueColor: 'error.main' }]
                                    : []),
                            ]}
                        />
                    )}
                    theme={theme}
                />
            </Box>
            {data.worst_offenders?.length > 0 && (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>Slowest to Disclose</Typography>
                    {data.worst_offenders.slice(0, 5).map((m, i) => (
                        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                            <Typography variant="body2" sx={{ color: 'text.primary' }}>{m.name}</Typography>
                            <Typography variant="body2" sx={{ fontFamily: t => t.typography.fontFamilyMono, color: m.avg_delay > 45 ? 'error.main' : 'text.secondary' }}>
                                {m.avg_delay} days avg ({m.trades} trades)
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </ChartSection>
    );
}
